import React, { useState, useCallback, useEffect } from 'react'
import { useUserInfo, useTransactionRefresh, useMerchantConfig } from '@/hooks'
import { toFixedNoRound } from '@/utils'
import { APIWeb } from '@/api'
import { Toast } from 'react-vant'
import CustomButton from '@/components/CustomButton'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import LogoTitle from '@/components/logoTitle'
import Ripple from '@/components/Ripple'

// 币种配置
const CURRENCIES = [
  { key: 'usdt', name: 'USDT', icon: '/images/home/usdt.png' },
  { key: 'usdc', name: 'USDC', icon: '/images/home/usdc.png' },
  { key: 'usd1', name: 'USD1', icon: '/images/home/usd1.png' },
  { key: 'usd', name: 'USD', icon: '/images/home/usd.png' }
] as const

type CurrencyKey = typeof CURRENCIES[number]['key']
type CardType = 'electronic' | 'physical'

export default React.memo(function ApplyCard() {
  const { userInfo, actions: userActions } = useUserInfo()
  const { merchantConfig, actions } = useMerchantConfig()
  const { refreshAfterTransaction } = useTransactionRefresh()
  const [loading, setLoading] = useState(false)
  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyKey>('usdt')
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // 获取卡片类型：electronic（虚拟卡）或 physical（实体卡）
  const cardType = (searchParams.get('type') as CardType) || 'electronic'

  // 根据卡片类型获取开卡费用
  const cardFee = cardType === 'electronic' ? merchantConfig?.card_fee : merchantConfig?.physical_card_fee

  // 邮寄地址 ID（仅实体卡需要，从 URL 参数获取）
  const addressIdFromUrl = searchParams.get('addressId') || ''
  const [shippingAddressId, setShippingAddressId] = useState<string>(addressIdFromUrl)

  // 设置邮寄地址 ID
  useEffect(() => {
    actions.fetchMerchantConfig()
    
    if (cardType === 'physical' && addressIdFromUrl) {
      setShippingAddressId(addressIdFromUrl)
    }
  }, [cardType, addressIdFromUrl])

  // 获取当前选择币种的余额
  const getCurrentBalance = useCallback(() => {
    if (!userInfo) return 0
    switch (selectedCurrency) {
      case 'usdt':
        return userInfo.usdt || 0
      case 'usd1':
        return userInfo.usd1 || 0
      case 'usdc':
        return userInfo.usdc || 0
      case 'usd':
        return userInfo.usd || 0
      default:
        return 0
    }
  }, [userInfo, selectedCurrency])

  // 页面加载时刷新用户信息
  useEffect(() => {
    userActions.refreshUserInfo()
  }, [])

  // 处理确认支付
  const handleConfirmPayment = useCallback(async () => {
    try {
      // 校验余额
      const balance = getCurrentBalance()
      if (balance <= 0) {
        Toast.info({
          message: t('余额不足请先充值'),
          position: 'middle'
        })
        return
      }

      // 实体卡需要校验邮寄地址
      if (cardType === 'physical' && !shippingAddressId) {
        Toast.info({
          message: t('请填写邮寄地址'),
          position: 'middle'
        })
        return
      }

      setLoading(true)

      // 根据卡片类型调用不同的接口
      if (cardType === 'physical') {
        // 调用实体卡申请接口
        await APIWeb.applyPhysicalCard({
          pay_type: selectedCurrency,
          shipping_address: Number(shippingAddressId)
        })
      } else {
        // 调用虚拟卡申请接口
        await APIWeb.applyElectronicCard({
          pay_type: selectedCurrency
        })
      }

      Toast.success({
        message: t('申请成功'),
        position: 'middle',
        onClose: () => {
          navigate('/card')
        }
      })
      // 交易成功后刷新状态
      await refreshAfterTransaction()

    } finally {
      setLoading(false)
    }
  }, [cardType, selectedCurrency, shippingAddressId, getCurrentBalance, refreshAfterTransaction, t, navigate])

  return (
    <PageLayout>
      <Header leftChildren={<Back />} centerChildren={<LogoTitle />} />
      <div className="px-6 pt-6">
        {/* Card Info Items */}
        <div className="text-[#808080] text-sm mb-3">{t('选择支付的账户')}</div>

        {/* 币种选择器 */}
        <div className="space-y-3 mb-6">
          {CURRENCIES.map((currency) => {
            const isSelected = selectedCurrency === currency.key
            const currencyBalance = (() => {
              switch (currency.key) {
                case 'usdt':
                  return userInfo?.usdt || 0
                case 'usd1':
                  return userInfo?.usd1 || 0
                case 'usdc':
                  return userInfo?.usdc || 0
                case 'usd':
                  return userInfo?.usd || 0
                default:
                  return 0
              }
            })()

            return (
              <Ripple className='rounded-2xl' key={currency.key}>
                <div
                  key={currency.key}
                  className={`bg-white rounded-2xl px-4 py-3 border border-solid cursor-pointer transition-all ${isSelected
                    ? 'border-[var(--primary-color)] bg-blue-50'
                    : 'border-[#000000]/10 hover:border-gray-300'
                    }`}
                  onClick={() => setSelectedCurrency(currency.key)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 mr-2 rounded-full overflow-hidden flex items-center justify-center">
                        <img className='w-full h-full object-cover' src={currency.icon} alt={currency.name} />
                      </div>
                      <div className="text-base text-[#1A1A1A]/65">{currency.name}</div>
                    </div>
                    <div className="text-right flex items-center">
                      <div className="flex flex-col items-end mr-2">
                        <div className="font-bold text-gray-900 text-base">
                          {toFixedNoRound(currencyBalance, 2)}
                        </div>
                        <div className="text-gray-500 text-xs">
                          ≈ {toFixedNoRound(currencyBalance, 2)} USD
                        </div>
                      </div>
                      <div className='ml-1'>
                        {isSelected ? (
                          <div className="w-3 h-3 justify-center items-center flex rounded-full bg-[var(--primary-color)]">
                          </div>

                        ) : (
                          <div className="w-3 h-3 justify-center items-center flex rounded-full"></div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Ripple>
            )
          })}
        </div>
        {/* 支付详情卡片 */}
        <div className="text-[#808080] text-sm mb-2">{t('支付详情')}</div>
        <div className="bg-white rounded-2xl py-4">
          <div className="flex justify-between items-center">
            <span className="text-[#7E7E7E] text-3">{t('卡片')}</span>
            <span className="text-[#7E7E7E] text-3.25">{cardFee} USD</span>
          </div>
          <div className='border-b border-b-solid border-[#000000]/10 my-3'></div>
          <div className="border-t border-gray-100"></div>
          <div className="flex justify-between items-center">
            <span className="text-3 text-[#7E7E7E]">{t('总计支付')}</span>
            <span className="text-3.75 text-black font-bold">{cardFee} USD</span>
          </div>
        </div>
        {/* 底部按钮区域 */}
        <div className="mt-6 mb-10">
          <CustomButton
            onClick={handleConfirmPayment}
            disabled={loading}
            loading={loading}
          >
            {loading ? t('处理中') : t('确认支付')}
          </CustomButton>
        </div>
      </div>
    </PageLayout>
  )
})
